import React, { useState, lazy, Suspense } from "react";
import { injectIntl, defineMessages } from "react-intl";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
} from "@material-ui/core";
import {
  CustomDocumentWithLinesIcon,
  CustomDocumentIcon,
  DownloadSlimIcon,
} from "../../../../common/icons";
import { handleDownloadTemplate, translations } from "./constants";
import { styles } from "./TemplateDownloadModal.styles";

const LoadInWidget = lazy(() => import("../../../../helpers/loadInWidget"));

const messages = defineMessages(translations);

export const DownloadBadge = ({ intl }) => (
  <div style={styles.badgeContainer}>
    <span style={{ fontWeight: 700 }}>
      {intl.formatMessage(messages.downloadedBadge)}
    </span>
    <span style={styles.badgeIcon}>
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="16" fill="none" />
        <path
          d="M23 12L15 20L11 16"
          stroke="#fff"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  </div>
);

export const TemplateCard = ({
  icon: Icon,
  title,
  description,
  isDownloaded,
  onDownload,
  intl,
}) => (
  <div style={styles.cardContainer}>
    <div style={styles.cardHeader}>
      <Icon />
      <div style={{ marginRight: "16px" }}>
        {isDownloaded && <DownloadBadge intl={intl} />}
      </div>
    </div>

    <Typography variant="h4" style={styles.cardTitle}>
      {title}
    </Typography>

    <Typography style={styles.cardDescription}>{description}</Typography>

    <div style={styles.cardButtonContainer}>
      <Button
        style={styles.downloadButton}
        className="v3-button black"
        onClick={onDownload}
        color="primary"
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {isDownloaded
            ? intl.formatMessage(messages.downloadAgain)
            : intl.formatMessage(messages.download)}
          <DownloadSlimIcon style={{ marginLeft: "15px" }} stroke="white" />
        </div>
      </Button>
    </div>
  </div>
);

function TemplateDownloadModal({
  open,
  onClose,
  companyId,
  integratorsSolutionsCode,
  intl,
  locale,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ empty: false, filled: false });

  const handleDownload = async (type) => {
    handleDownloadTemplate({
      type,
      companyId,
      integratorsSolutionsCode,
      locale,
      setIsLoading,
      onSuccess: (t) => setStatus((prev) => ({ ...prev, [t]: true })),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth={true}
      PaperProps={{ style: styles.dialogPaper }}
    >
      {isLoading && (
        <Suspense fallback={<div />}>
          <div style={styles.loadingOverlay}>
            <LoadInWidget />
          </div>
        </Suspense>
      )}

      <DialogTitle id="alert-dialog-title" style={styles.dialogTitle}>
        <Typography variant="h4" style={styles.titleText}>
          {intl.formatMessage(messages.title)}
        </Typography>
        <button onClick={onClose} style={styles.closeButton}>
          <span aria-hidden="true">&times;</span>
        </button>
      </DialogTitle>

      <DialogContent>
        <Typography style={styles.subtitle}>
          {intl.formatMessage(messages.subtitle)}
        </Typography>

        <div style={{ display: "flex", marginTop: "2rem" }}>
          <div style={{ marginRight: "32px" }}>
            <TemplateCard
              intl={intl}
              icon={CustomDocumentIcon}
              title={intl.formatMessage(messages.emptySheetTitle)}
              description={intl.formatMessage(messages.emptySheetDescription)}
              isDownloaded={status.empty}
              onDownload={() => handleDownload("empty")}
            />
          </div>

          <TemplateCard
            intl={intl}
            icon={CustomDocumentWithLinesIcon}
            title={intl.formatMessage(messages.filledSheetTitle)}
            description={intl.formatMessage(messages.filledSheetDescription)}
            isDownloaded={status.filled}
            onDownload={() => handleDownload("filled")}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default injectIntl(TemplateDownloadModal);
